import { useQuery } from '@tanstack/react-query';
import { ApiResponse, useMemoizedArrayData } from 'hooks/api/api';
import {
  AutomationsWorkflowsTriggersService,
  TCWorkflowTrigger,
  WorkflowMetadata,
} from 'src/api/generated';
import { useAutomationsWorkflows } from './workflows';

interface AutomationItem {
  workflowId: string;
  workflowName?: string;
  version: string;
  trigger: TCWorkflowTrigger;
}

interface UseTenantAutomationsResponse extends ApiResponse {
  automations: AutomationItem[];
}

export const useTenantAutomations = (
  version: string,
  limit?: number,
  enabled = true
): UseTenantAutomationsResponse => {
  const {
    workflows,
    loading: workflowsLoading,
    error: workflowsError,
  } = useAutomationsWorkflows(limit, enabled);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      'automations/tenant-automations',
      {
        version,
        workflowIds: workflows
          .map((w: WorkflowMetadata) => w.workflowId)
          .sort(),
      },
    ],
    enabled: enabled && !workflowsLoading && workflows.length > 0,
    queryFn: async () => {
      const results = await Promise.allSettled(
        workflows.map(async (w: WorkflowMetadata) => {
          const res =
            await AutomationsWorkflowsTriggersService.listWorkflowTriggers(
              w.workflowId,
              version
            );
          return { workflow: w, response: res };
        })
      );

      const items: AutomationItem[] = [];
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        const { workflow, response } = r.value;
        for (const t of response.items || []) {
          items.push({
            workflowId: workflow.workflowId,
            workflowName: workflow.name,
            version,
            trigger: t,
          });
        }
      }
      return items;
    },
    staleTime: 15_000,
  });

  const automations = useMemoizedArrayData<AutomationItem>(data || []);

  return {
    loading: workflowsLoading || isLoading,
    error: (error as Error) || (workflowsError as Error) || null,
    refetch,
    automations,
  };
};
